import React, { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { useCredit } from "../../context/CreditContext";
import { CASE_THUMBS, optimize, fmtUsd } from "../../data/casesData";
import { Crown, Check, Lock, Gift, Loader2, Trophy } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authCfg = () => {
  const t = localStorage.getItem("uc_token");
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

export default function VipModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const { refreshCredits } = useCredit();
  const [tab, setTab] = useState("ranks");
  const [data, setData] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [msg, setMsg] = useState("");

  const load = async () => {
    if (!user) { setData(null); return; }
    try { const r = await axios.get(`${API}/vip`, authCfg()); setData(r.data); } catch { /* ignore */ }
  };
  useEffect(() => { if (open) { setMsg(""); load(); } /* eslint-disable-next-line */ }, [open, user]);

  const claim = async (tierKey) => {
    setClaiming(tierKey); setMsg("");
    try {
      const r = await axios.post(`${API}/vip/claim/${tierKey}`, {}, authCfg());
      setMsg(`Reward claimed: ${r.data.item.name} (${fmtUsd(r.data.item.value)}) added to your inventory`);
      refreshCredits(); load();
    } catch (e) { setMsg(e.response?.data?.detail || "Could not claim reward"); }
    finally { setClaiming(null); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vip-members-modal" className="max-w-lg rounded-2xl border-[var(--uc-border)] bg-[#15112A] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl font-extrabold">
            <Crown className="h-5 w-5 text-[#FFD700]" /> VIP Members
          </DialogTitle>
        </DialogHeader>

        {!user ? (
          <div className="py-6 text-center">
            <p className="text-sm text-[var(--uc-muted)]">Sign in to see your rank and claim rewards.</p>
            <button onClick={() => { onOpenChange(false); window.dispatchEvent(new Event("uc:open-auth")); }}
              className="mt-4 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] px-5 py-2.5 text-sm font-bold text-white">Sign in</button>
          </div>
        ) : !data ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[var(--uc-muted)]" /></div>
        ) : (
          <>
            {/* rank header */}
            <div className="rounded-xl border border-[var(--uc-border)] bg-[var(--uc-card)] p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: `${data.rank.color}22` }}>
                    <Trophy className="h-4 w-4" style={{ color: data.rank.color }} />
                  </span>
                  <div>
                    <div className="text-sm font-extrabold" style={{ color: data.rank.color }}>{data.rank.name}</div>
                    <div className="text-[11px] text-[var(--uc-muted)]">Wagered {fmtUsd(data.wagered)}</div>
                  </div>
                </div>
                {data.next && <div className="text-right text-[11px] text-[var(--uc-muted)]">Next: <span className="font-bold text-white">{data.next.name}</span><br />{fmtUsd(data.to_next)} to go</div>}
              </div>
              <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
                <div data-testid="vip-progress-bar" className="h-full rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#C4B5FD] transition-all" style={{ width: `${data.progress}%` }} />
              </div>
            </div>

            <div className="mt-1 flex items-center rounded-xl border border-[var(--uc-border)] bg-[var(--uc-rail)] p-1">
              <button data-testid="vip-tab-ranks" onClick={() => setTab("ranks")} className={`flex-1 rounded-lg py-1.5 text-xs font-semibold ${tab === "ranks" ? "bg-[var(--uc-primary)] text-white" : "text-[var(--uc-muted)]"}`}>Ranks</button>
              <button data-testid="vip-tab-rewards" onClick={() => setTab("rewards")} className={`flex-1 rounded-lg py-1.5 text-xs font-semibold ${tab === "rewards" ? "bg-[var(--uc-primary)] text-white" : "text-[var(--uc-muted)]"}`}>Rewards</button>
            </div>

            {msg && <div className="rounded-lg bg-white/5 px-3 py-2 text-[12px] text-[#C4B5FD]">{msg}</div>}

            {tab === "ranks" ? (
              <div className="grid grid-cols-1 gap-2">
                {data.tiers.map((t) => (
                  <div key={t.key} className={`flex items-center justify-between rounded-xl border p-3 ${data.rank.key === t.key ? "border-[var(--uc-border-active)] bg-white/5" : "border-[var(--uc-border)] bg-[var(--uc-card)]"}`}>
                    <div className="flex items-center gap-2">
                      <span className="h-2.5 w-2.5 rounded-full" style={{ background: t.color }} />
                      <span className="text-sm font-bold" style={{ color: t.color }}>{t.name}</span>
                    </div>
                    <span className="text-[11px] text-[var(--uc-muted)]">{fmtUsd(t.min)} wagered</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {data.rewards.map((r) => (
                  <div key={r.tier} data-testid={`vip-reward-${r.tier}`} className="relative flex flex-col rounded-xl border border-[var(--uc-border)] bg-[var(--uc-card)] p-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: r.tier_color }}>{r.tier_name}</span>
                      {!r.unlocked && <Lock className="h-3.5 w-3.5 text-[var(--uc-muted)]" />}
                    </div>
                    <div className="relative flex h-24 items-center justify-center overflow-hidden rounded-lg bg-black/20">
                      <img src={optimize(CASE_THUMBS[r.case_id], 300)} alt={r.case_name} className={`h-[90%] object-contain ${r.unlocked ? "" : "opacity-30 grayscale"}`} />
                    </div>
                    <div className="mt-1.5 truncate text-[12px] font-semibold text-white">{r.case_name}</div>
                    {r.claimed ? (
                      <div className="mt-2 flex items-center justify-center gap-1 rounded-lg bg-[var(--uc-green)]/15 py-1.5 text-[11px] font-bold text-[var(--uc-green)]"><Check className="h-3.5 w-3.5" /> Claimed</div>
                    ) : (
                      <button disabled={!r.unlocked || claiming === r.tier} onClick={() => claim(r.tier)} data-testid={`vip-claim-${r.tier}`}
                        className="mt-2 flex items-center justify-center gap-1 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] py-1.5 text-[11px] font-bold text-white disabled:opacity-40">
                        {claiming === r.tier ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Gift className="h-3.5 w-3.5" />} {r.unlocked ? "Claim" : "Locked"}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
